import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LuPrinter } from "react-icons/lu";
import { CustomInput } from "../../components/CustomInput";
import TablePagination from "../../components/TablePagination";
import useFetch from "../../hooks/useFetch";
import { getInvoices } from "../../services/invoice";
import { formatDate, formatMoney } from "../../utils/format";
import { printInvoices } from "./invoiceDocument";
import dashboardStyles from "../../styles/dashboard.module.css";
import styles from "../../styles/page.module.css";

// Every generated invoice, newest first. Search by invoice no (or customer / an order no on it), narrow by date,
// open an invoice to see its orders, print it again exactly as it was generated.
const InvoiceHistoryPage = () => {
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [range, setRange] = useState({ from: "", to: "" });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setQuery(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, loading } = useFetch(
    () => getInvoices({ searchString: query, fromDate: range.from, toDate: range.to, page, limit }),
    [query, range.from, range.to, page, limit]
  );
  const invoices = data?.data || [];
  const total = data?.meta?.total || 0;

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Invoice History</h1>
          <p className={styles.pageSub}>Every invoice that was generated, with its invoice no - search it, see its orders, print it again</p>
        </div>
        <div className={styles.pageActions}>
          <Link to="/dashboard/invoice-print" className={`${dashboardStyles.btn} ${dashboardStyles.btnPrimary}`} style={{ textDecoration: "none" }}>
            New Invoice
          </Link>
        </div>
      </div>

      <section className={dashboardStyles.card}>
        <div className={styles.toolbar} style={{ alignItems: "flex-end", flexWrap: "wrap", gap: 12 }}>
          <div style={{ minWidth: 260, flex: "1 1 260px" }}>
            <label className="form-label" htmlFor="history-search">
              Search
            </label>
            <CustomInput id="history-search" placeholder="Invoice no / customer / order no" value={search} onChange={(event) => setSearch(event.target.value)} sx={{ "& .MuiInputBase-root": { height: "38px" } }} />
          </div>
          <div className={styles.dateFilter}>
            <label className="form-label" htmlFor="history-from">
              From
            </label>
            <CustomInput id="history-from" type="date" value={range.from} onChange={(event) => { setRange({ ...range, from: event.target.value }); setPage(1); }} sx={{ "& .MuiInputBase-root": { height: "38px" } }} />
          </div>
          <div className={styles.dateFilter}>
            <label className="form-label" htmlFor="history-to">
              To
            </label>
            <CustomInput id="history-to" type="date" value={range.to} onChange={(event) => { setRange({ ...range, to: event.target.value }); setPage(1); }} sx={{ "& .MuiInputBase-root": { height: "38px" } }} />
          </div>
        </div>

        <div className={dashboardStyles.tableWrap} style={{ opacity: loading ? 0.5 : 1, marginTop: 12 }}>
          <table className={dashboardStyles.table} style={{ minWidth: 900 }}>
            <thead>
              <tr>
                <th>Invoice No</th>
                <th>Date</th>
                <th>Customer</th>
                <th style={{ textAlign: "right" }}>Orders</th>
                <th style={{ textAlign: "right" }}>Total</th>
                <th style={{ textAlign: "right" }}>Paid</th>
                <th style={{ textAlign: "right" }}>Due</th>
                <th>Created By</th>
                <th style={{ width: 90 }}>Print</th>
              </tr>
            </thead>
            <tbody>
              {invoices.length === 0 && !loading ? (
                <tr>
                  <td colSpan={9} className={dashboardStyles.emptyRow}>
                    {query || range.from || range.to ? "No invoice found" : "No invoice has been generated yet"}
                  </td>
                </tr>
              ) : (
                invoices.flatMap((invoice) => {
                  const open = openId === invoice.id;
                  return [
                    <tr key={invoice.id}>
                      <td>
                        <button
                          type="button"
                          onClick={() => setOpenId(open ? null : invoice.id)}
                          aria-expanded={open}
                          style={{ background: "none", border: 0, padding: 0, color: "var(--color-primary)", fontWeight: 600, cursor: "pointer" }}
                        >
                          {invoice.invoiceNo}
                        </button>
                      </td>
                      <td>{formatDate(invoice.createdDateTime, "DD/MMM/YYYY hh:mm A")}</td>
                      <td>{[invoice.customerCode, invoice.customerName].filter(Boolean).join(" - ") || "-"}</td>
                      <td style={{ textAlign: "right" }}>{invoice.orderCount}</td>
                      <td style={{ textAlign: "right" }}>{formatMoney(invoice.total)}</td>
                      <td style={{ textAlign: "right", color: "#15803d" }}>{formatMoney(invoice.paid)}</td>
                      <td style={{ textAlign: "right", color: invoice.due > 0 ? "#dc2626" : undefined }}>{formatMoney(invoice.due)}</td>
                      <td>{invoice.createdBy || "-"}</td>
                      <td>
                        <button type="button" className={`${dashboardStyles.btn} ${dashboardStyles.btnOutline}`} onClick={() => printInvoices([invoice])} aria-label={`Print ${invoice.invoiceNo}`}>
                          <LuPrinter />
                        </button>
                      </td>
                    </tr>,
                    open && (
                      <tr key={`${invoice.id}-orders`}>
                        <td colSpan={9} style={{ background: "var(--color-bg-subtle, #f9fafb)" }}>
                          <table className={dashboardStyles.table} style={{ minWidth: 700 }}>
                            <thead>
                              <tr>
                                <th>Order</th>
                                <th>Order Date</th>
                                <th style={{ textAlign: "right" }}>Qty</th>
                                <th style={{ textAlign: "right" }}>Shipping</th>
                                <th style={{ textAlign: "right" }}>Discount</th>
                                <th style={{ textAlign: "right" }}>Total</th>
                                <th style={{ textAlign: "right" }}>Paid</th>
                                <th style={{ textAlign: "right" }}>Due</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(invoice.lines || []).map((line) => (
                                <tr key={line.orderId}>
                                  <td>
                                    <Link to={`/dashboard/order/${line.orderId}`} style={{ color: "var(--color-primary)", textDecoration: "none" }}>
                                      {line.code}
                                    </Link>
                                  </td>
                                  <td>{formatDate(line.orderDate, "DD/MMM/YYYY")}</td>
                                  <td style={{ textAlign: "right" }}>{line.quantity}</td>
                                  <td style={{ textAlign: "right" }}>{formatMoney(line.cnCourier + line.shipping + line.bdCourier)}</td>
                                  <td style={{ textAlign: "right" }}>{formatMoney(line.discount)}</td>
                                  <td style={{ textAlign: "right" }}>{formatMoney(line.total)}</td>
                                  <td style={{ textAlign: "right" }}>{formatMoney(line.paid)}</td>
                                  <td style={{ textAlign: "right" }}>{formatMoney(line.due)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    ),
                  ];
                })
              )}
            </tbody>
          </table>
        </div>

        <TablePagination
          page={page}
          limit={limit}
          total={total}
          onPageChange={setPage}
          onLimitChange={(value) => {
            setLimit(value);
            setPage(1);
          }}
        />
      </section>
    </div>
  );
};

export default InvoiceHistoryPage;
